import { tryOnScopeDispose, useIntervalFn, useMutationObserver } from "@vueuse/core";
import { ref, watch } from "vue";
import { usePurchaseStore } from "#ce/components/SpCheckout/store/purchase";

let linkFrameWatcher = null;
let challengeFrameWatcher = null;

let legacyModal = null;
const isHiddenTemporarily = ref(false);

/**
 * Observes the DOM for the presence of a challenge frame and sets the
 * `isHiddenTemporarily` state to true when one is detected.
 * `isHiddenTemporarily` is shared so PaymentPanel can set it and SpCheckout
 * can read it to hide/show the modal.
 * It also watches for the presence of a legacy modal and sets
 * `isHiddenTemporarily` to true while it is present.
 *
 * In particular, Stripe’s challenge frame is used to collect additional
 * credit card details or to activate Link payments. This opens a new modal
 * in the same position as the checkout modal.
 * However, because the checkout modal is implemented as a native <dialog>,
 * it will always obscure the legacy modal.
 *
 * This watcher is therefore used to detect when a legacy modal is opened
 * (and set `isHiddenTemporarily` to true) and when the legacy modal is
 * closed (and set `isHiddenTemporarily` to false).
 *
 * The `isHiddenTemporarily` state is used to hide the checkout modal while
 * a legacy modal is open, and to show it again once the legacy modal is
 * closed.
 *
 * @returns {Object}
 * @property {Ref<boolean>} isHiddenTemporarily
 */
export function useModalWatcher() {
  const purchaseStore = usePurchaseStore();

  function startChallengeWatcher() {
    const watchInterval = 400;
    const maxRetries = 500;

    let retries = 0;

    const watchHandler = () => {
      const frames = Array.from(document.getElementsByTagName("iframe"));
      const challengeFrame = frames.find(({ src }) => src.includes("-challenge"));

      if (challengeFrame) {
        isHiddenTemporarily.value = true;
      }

      if (retries >= maxRetries || challengeFrame) {
        stopChallengeWatcher();
      }

      retries++;
    };

    challengeFrameWatcher = useIntervalFn(watchHandler, watchInterval);
  }

  function stopChallengeWatcher() {
    challengeFrameWatcher?.pause();
    challengeFrameWatcher = null;
  }

  watch(purchaseStore.aboutToConfirmPayment, (value) => {
    if (value) {
      startChallengeWatcher();
    } else {
      stopChallengeWatcher();
      cleanup();
    }
  });

  function startLinkFrameWatcher() {
    const options = {
      childList: true,
    };

    linkFrameWatcher = useMutationObserver(document.body, handleLinkFrameMutation, options);
  }

  function handleLinkFrameMutation([mutation]) {
    const addedNode = mutation.addedNodes?.[0];
    const removedNode = mutation.removedNodes?.[0];

    if (addedNode?.childNodes?.[0]?.nodeName === "IFRAME") {
      isHiddenTemporarily.value = true;
      legacyModal = addedNode;
    } else if (removedNode === legacyModal) {
      isHiddenTemporarily.value = false;
      legacyModal = null;
    }
  }

  function stopLinkFrameWatcher() {
    linkFrameWatcher?.stop();
    linkFrameWatcher = null;
  }

  watch(purchaseStore.paymentType, (value) => {
    if (value === "link") {
      startLinkFrameWatcher();
    } else {
      stopLinkFrameWatcher();
      cleanup();
    }
  });

  function cleanup() {
    isHiddenTemporarily.value = false;
  }

  function stop() {
    stopChallengeWatcher();
    stopLinkFrameWatcher();
    cleanup();
  }

  tryOnScopeDispose(() => {
    stop();
  });

  return {
    // State
    isHiddenTemporarily,
  };
}
